// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * One BOM version (design doc §6.2.1, §7.3, §8.2, §9.3, US-E-02…05).
 *
 * A draft is editable and a published version is not, and the page is really two pages
 * wearing one URL. That asymmetry is the design's central rule about BOMs: a published
 * version is what filed archives were measured against, so it is frozen, and the way to
 * change it is to derive a new draft from it.
 *
 * Two columns sit side by side for a reason. `quantity` is usage per parent and
 * `effective_quantity` is usage per machine — the first multiplied through every ancestor.
 * A three-deep assembly where each level uses two of the next reads as "2" on every line
 * and needs eight of the leaf part. The slot count a build must fill comes from the
 * effective column, so both are shown and the hint says which is which.
 *
 * A draft mid-edit can be momentarily unresolvable — a line whose parent has not been
 * entered yet — and the server answers that with an empty set of effective quantities
 * rather than an error, so this page has to render a dash there and keep working.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { NAME_MAX_LEN } from "@/api/constraints";
import { api } from "@/api/endpoints";
import type { BomItem } from "@/api/types";
import { AddLabel } from "@/components/AddLabel";
import { BarcodeRulesDialog } from "@/components/BarcodeRulesDialog";
import { BomItemDialog, type BomItemDraft } from "@/components/BomItemDialog";
import { BomModulesSummary, moduleTypeLabel } from "@/components/BomModulesSummary";
import { ErrorBanner } from "@/components/ErrorBanner";
import { FoldControls } from "@/components/FoldControls";
import { IconButton } from "@/components/IconButton";
import { CheckIcon, ChevronIcon, CloseIcon, EditIcon, TrashIcon } from "@/components/icons";
import { Modal } from "@/components/Modal";
import { RemarkCell } from "@/components/RemarkCell";
import { ResizableTable } from "@/components/ResizableTable";
import { StatusBadge } from "@/components/StatusBadge";
import { usePermissions } from "@/hooks/usePermissions";
import { type Locale, SUPPORTED_LOCALES } from "@/i18n";
import { useBarcodeRules } from "@/scanner/barcodeRules";
import { foldedToLevel, hiddenKeys, levelCount, parentKeys } from "@/tree/fold";

export function BomDetailPage() {
  const { t, i18n } = useTranslation();
  const { canManageMasterData } = usePermissions();
  const { bomId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /** null = closed, "new" = adding, otherwise the line being edited. */
  const [editing, setEditing] = useState<BomItem | "new" | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // The name the version is published under, typed in the publish dialog; null while the
  // dialog is closed.
  const [publishName, setPublishName] = useState<string | null>(null);
  // The version typed in the publish dialog — prefilled with the draft's own number, else
  // the suggestion. A draft has no number until a person types one (component design §4.7).
  const [publishVersion, setPublishVersion] = useState("");
  // The version box opened from the header pencil on a draft; null while closed.
  const [editingVersion, setEditingVersion] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  // Opens on the critical parts — the ones with a serial, which is what this list is read
  // for — unless there are none (a machine filed without a BOM may have only counted
  // parts), where that would open on an empty list. `null` until the operator picks.
  const [chosenKind, setShownKind] = useState<LineKind | null>(null);
  // One module's lines, or the robot's own — the BOM read module by module (§12). "" is
  // everything; "-" the lines outside every module.
  const [shownModule, setShownModule] = useState("");
  // Which critical lines are opened into their slots. Closed by default: a 400-line BOM
  // opened in full would bury the lines among their slots.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  // Which language's remarks this page shows and edits — picked in the remark column's
  // header, the UI's language until then. Remarks are kept per language (§7.3), and
  // writing the English ones is a job done here, from a Chinese screen as often as not.
  const [remarkLocale, setRemarkLocale] = useState<Locale>(() =>
    i18n.language === "en-US" ? "en-US" : "zh-CN",
  );
  // Which lines are folded, hiding the lines under them. Open by default, as before.
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());
  const toggleFold = (itemId: string) =>
    setFolded((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  const toggle = (itemId: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });

  const bom = useQuery({
    // Under ["bom", bomId], so every invalidation of the BOM covers each language.
    queryKey: ["bom", bomId, remarkLocale],
    queryFn: () => api.boms.get(bomId, remarkLocale),
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["bom", bomId] });
  // Under ["bom", bomId], so every edit that can move a line refreshes the counts too.
  const modules = useQuery({
    queryKey: ["bom", bomId, "modules"],
    queryFn: () => api.boms.modules(bomId),
    enabled: Boolean(bom.data),
    retry: false,
  });
  const moduleTypes = useQuery({
    queryKey: ["module-types"],
    queryFn: () => api.moduleTypes.list(),
    staleTime: Number.POSITIVE_INFINITY,
  });
  // The critical lines' materials' barcode rules — kept beside the BOM, so editable on a
  // published one too. Every line of one material shares them.
  const barcodeRules = useBarcodeRules(
    useMemo(
      () =>
        (bom.data?.items ?? [])
          .filter((item) => item.is_critical)
          .map((item) => item.material_code),
      [bom.data],
    ),
  );
  // The material whose rules are open; null while the dialog is closed.
  const [rulesOf, setRulesOf] = useState<string | null>(null);
  const setModuleType = useMutation({
    mutationFn: ({ itemId, moduleType }: { itemId: string; moduleType: string | null }) =>
      api.boms.setModuleType(bomId, itemId, moduleType),
    onSuccess: invalidate,
  });

  // Only worth asking for while the draft has no number of its own.
  const suggestion = useQuery({
    queryKey: ["bom", bomId, "suggested-version"],
    queryFn: () => api.boms.suggestedVersion(bomId),
    enabled: bom.data?.status === "DRAFT" && !bom.data.version,
  });
  const suggestedVersion = suggestion.data?.version ?? "";
  const setVersion = useMutation({
    mutationFn: (version: string) => api.boms.setVersion(bomId, version),
    onSuccess: async () => {
      setEditingVersion(null);
      await invalidate();
    },
  });
  const publish = useMutation({
    mutationFn: (name: string) => api.boms.publish(bomId, name, publishVersion),
    onSuccess: async () => {
      setPublishName(null);
      await invalidate();
    },
  });
  const derive = useMutation({
    mutationFn: () => api.boms.derive(bomId),
    onSuccess: (created) => navigate(`/boms/${created.id}`),
  });
  const removeBom = useMutation({
    mutationFn: () => api.boms.remove(bomId),
    onSuccess: () => navigate("/boms"),
  });
  const setCritical = useMutation({
    mutationFn: ({ itemId, isCritical }: { itemId: string; isCritical: boolean }) =>
      api.boms.setCritical(bomId, itemId, isCritical),
    onSuccess: invalidate,
  });
  const saveItem = useMutation({
    mutationFn: ({ item, draft }: { item: BomItem | null; draft: BomItemDraft }) => {
      const body = {
        position_code: draft.position_code.trim(),
        material_info: draft.material_info.trim(),
        // Blank: the server fills in the parent line's name (a top-level line's own).
        part_name: draft.part_name.trim() || null,
        drawing_no: draft.drawing_no.trim() || null,
        quantity: draft.quantity,
        material_code: draft.material_code.trim() || null,
        is_critical: draft.is_critical,
        // Sent even when empty, so clearing the box removes the remark — in the language
        // the page is showing, which is the one the dialog's remark box is labelled with.
        remark: draft.remark.trim() || null,
        locale: remarkLocale,
      };
      if (!item) {
        return api.boms.addItem(bomId, { ...body, parent_item_id: draft.parent_item_id || null });
      }
      // An absent `parent_item_id` cannot say "no parent" in a PATCH — omission means
      // "leave it alone" — so clearing it is the explicit `move_to_root` flag.
      const updated = api.boms.updateItem(bomId, item.id, {
        ...body,
        ...(draft.parent_item_id
          ? { parent_item_id: draft.parent_item_id }
          : { move_to_root: true }),
      });
      // The module type has an endpoint of its own (it checks the structure the line
      // sits in), set after the line itself only when it changed.
      if ((item.module_type ?? "") === draft.module_type) return updated;
      return updated.then(async (saved) => {
        await api.boms.setModuleType(bomId, item.id, draft.module_type || null);
        return saved;
      });
    },
    onSuccess: async () => {
      setEditing(null);
      await invalidate();
    },
  });
  const deleteItem = useMutation({
    mutationFn: (itemId: string) => api.boms.deleteItem(bomId, itemId),
    onSuccess: async () => {
      setPendingDelete(null);
      await invalidate();
    },
  });

  if (bom.isLoading) return <p className="muted">{t("common.loading")}</p>;
  if (bom.error) return <ErrorBanner error={bom.error} />;
  if (!bom.data) return null;

  const detail = bom.data;
  const shownKind: LineKind =
    chosenKind ?? (detail.items.some((item) => item.is_critical) ? "critical" : "all");
  const needle = query.trim().toLowerCase();
  const inModule = (item: BomItem) =>
    shownModule === "" || (item.owner_module_type ?? "-") === shownModule;
  const shownItems = detail.items.filter(
    (item) =>
      isShown(item, shownKind) &&
      inModule(item) &&
      (!needle || lineMatches(item, needle) || slotMatches(item, needle)),
  );
  const narrowed = needle !== "" || shownKind !== "all" || shownModule !== "";
  const markedModules = [
    ...new Set(
      detail.items.map((item) => item.module_type).filter((code): code is string => !!code),
    ),
  ];
  const nodes = detail.items.map((item) => ({
    key: item.id,
    parentKey: item.parent_item_id,
    depth: depthOf(item),
  }));
  const parents = parentKeys(nodes);
  // While filtering or searching every match is shown, wherever it sits in the tree.
  const hidden = narrowed ? new Set<string>() : hiddenKeys(nodes, folded);
  const visibleItems = shownItems.filter((item) => !hidden.has(item.id));
  const isDraft = detail.status === "DRAFT";
  // Lines are changed only on a draft, and only by an administrator (§12.4).
  const editable = isDraft && canManageMasterData;
  const criticalCount = detail.items.filter((item) => item.is_critical).length;
  const slotCount = detail.items
    .filter((item) => item.is_critical)
    .reduce((total, item) => total + (item.effective_quantity ?? 0), 0);

  return (
    <section>
      <div className="page-head">
        <h1 className="record-header">
          {detail.version ? (
            <span>v{detail.version}</span>
          ) : (
            <span className="muted">{t("bom.unnumbered")}</span>
          )}
          {editable && (
            <IconButton
              label={t("bom.setVersion")}
              icon={<EditIcon />}
              onClick={() => setEditingVersion(detail.version ?? suggestedVersion)}
            />
          )}
          {detail.name && <span className="muted">{detail.name}</span>}
          <StatusBadge status={detail.status} />
          {detail.disabled_at && <StatusBadge status="DISABLED" />}
          {detail.root_material_code && (
            <span className="muted mono" title={t("bom.rootMaterialCode")}>
              {detail.root_material_code}
            </span>
          )}
        </h1>
        <div className="page-actions" hidden={!canManageMasterData}>
          {isDraft ? (
            <>
              <button type="button" onClick={() => setEditing("new")}>
                <AddLabel>{t("bom.addItem")}</AddLabel>
              </button>
              <button
                type="button"
                className="button-primary"
                disabled={
                  publish.isPending || detail.items.length === 0 || Boolean(detail.disabled_at)
                }
                onClick={() => {
                  setPublishVersion(detail.version ?? suggestedVersion);
                  setPublishName(bom.data?.name ?? "");
                }}
              >
                {t("bom.publish")}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="button-primary"
              disabled={derive.isPending}
              onClick={() => derive.mutate()}
            >
              {t("bom.derive")}
            </button>
          )}
        </div>
      </div>

      {detail.disabled_at && <p className="hint hint-error">{t("bom.disabledHint")}</p>}

      <p className="record-meta">
        {isDraft
          ? t("bom.draftHint", { critical: criticalCount, slots: slotCount })
          : t("bom.publishedHint")}
      </p>

      <ErrorBanner error={derive.error ?? setCritical.error ?? deleteItem.error} />

      <p className="hint">{t("bom.effectiveHint")}</p>

      {/* The whole BOM is already on the page, so this filters it in place: by position
          code, name, material code or name, and remark — a slot's own remark included,
          in which case that line opens to show the slot that matched. */}
      <div className="filters">
        <input
          type="search"
          aria-label={t("common.search")}
          autoComplete="off"
          placeholder={t("bom.findLine")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {/* The filing screen's chips, counted over the whole BOM: a filter answers "show
            me less", never "there is less". Combines with the search. */}
        <div className="chip-group" role="group" aria-label={t("archive.showRows")}>
          {LINE_KINDS.map((option) => (
            <button
              key={option}
              type="button"
              className={shownKind === option ? "chip chip-on" : "chip"}
              aria-pressed={shownKind === option}
              onClick={() => setShownKind(option)}
            >
              {t(`archive.filter.${option}`, {
                n: detail.items.filter((item) => isShown(item, option)).length,
              })}
            </button>
          ))}
        </div>
        {markedModules.length > 0 && (
          <select
            aria-label={t("bom.modules.module")}
            value={shownModule}
            onChange={(event) => setShownModule(event.target.value)}
          >
            <option value="">{t("bom.modules.allLines")}</option>
            <option value="-">{t("bom.modules.robotOnly")}</option>
            {markedModules.map((code) => (
              <option key={code} value={code}>
                {moduleTypeLabel(code, moduleTypes.data ?? [], i18n.language === "en-US")}
              </option>
            ))}
          </select>
        )}
        <FoldControls
          levels={levelCount(nodes)}
          disabled={narrowed}
          onLevel={(level) => setFolded(level === null ? new Set() : foldedToLevel(nodes, level))}
        />
        {narrowed && (
          <span className="muted">
            {t("bom.shownLines", { n: shownItems.length, total: detail.items.length })}
          </span>
        )}
      </div>

      {/* Keyed anew whenever the columns move: saved widths are matched by position. */}
      {/* What each filing scans, once there is a module to tell the robot's count apart. */}
      {modules.data && modules.data.modules.length > 0 && (
        <BomModulesSummary modules={modules.data} types={moduleTypes.data ?? []} />
      )}
      <ErrorBanner error={setModuleType.error} />

      <ResizableTable storageKey="bom-detail-3">
        <thead>
          <tr>
            {/* Where on the machine, first: the list is read part by part. */}
            <th className="col-part-name">{t("bom.partName")}</th>
            <th>{t("bom.positionCode")}</th>
            <th>{t("bom.positionName")}</th>
            <th>{t("bom.drawingNo")}</th>
            {/* Only an assembly — a line with lines under it — can be a module. */}
            <th>{t("bom.moduleType")}</th>
            <th>{t("bom.materialCode")}</th>
            <th>{t("bom.quantity")}</th>
            <th>{t("bom.effectiveQuantity")}</th>
            <th>{t("bom.critical")}</th>
            <th>{t("bom.barcodeRules")}</th>
            {/* Its own column on every BOM, empty ones included: a draft with no remarks
                yet has to show where they go, or the feature looks absent. */}
            {/* The language these remarks are in, chosen here: what is shown and what
                is edited, on this page only. Every other screen shows the UI's. */}
            <th className="line-remark-col">
              <span className="remark-head">
                {t("bom.remark")}
                <select
                  aria-label={t("bom.remarkLanguage")}
                  value={remarkLocale}
                  onChange={(event) => setRemarkLocale(event.target.value as Locale)}
                >
                  {SUPPORTED_LOCALES.map((locale) => (
                    <option key={locale} value={locale}>
                      {t(`bom.remarkLanguages.${locale}`)}
                    </option>
                  ))}
                </select>
              </span>
            </th>
            {editable && <th />}
          </tr>
        </thead>
        <tbody>
          {visibleItems.map((item) => {
            const slots = slotCountOf(item);
            const open = expanded.has(item.id) || (needle !== "" && slotMatches(item, needle));
            return (
              <Fragment key={item.id}>
                <tr>
                  <td>{item.part_name || "—"}</td>
                  {/* Not indented: the codes stay in one column to scan down. The hierarchy
                  is the fold before each parent line (and 全部展开 / 全部折叠). */}
                  <td className="mono">
                    {/* The tree's own fold: a line with lines under it hides them. */}
                    {parents.has(item.id) && !narrowed ? (
                      <button
                        type="button"
                        className={
                          folded.has(item.id)
                            ? "row-toggle tooltip-trigger"
                            : "row-toggle row-toggle-open tooltip-trigger"
                        }
                        aria-expanded={!folded.has(item.id)}
                        aria-label={t(folded.has(item.id) ? "tree.expand" : "tree.collapse", {
                          name: item.position_code,
                        })}
                        data-tooltip={t(folded.has(item.id) ? "tree.expand" : "tree.collapse", {
                          name: item.position_code,
                        })}
                        onClick={() => toggleFold(item.id)}
                      >
                        <ChevronIcon size={14} />
                      </button>
                    ) : (
                      <span className="row-toggle-spacer" />
                    )}
                    {item.position_code}
                    {/* A critical line of more than one slot opens into its slots, each with
                    its own remark — three identical motors that go to three joints. A pill
                    after the code, so it is not mistaken for the tree's fold before it. */}
                    {slots > 1 && (
                      <button
                        type="button"
                        className={
                          open
                            ? "slot-toggle slot-toggle-open tooltip-trigger"
                            : "slot-toggle tooltip-trigger"
                        }
                        aria-expanded={open}
                        aria-label={t("bom.expandSlots", { line: item.position_code, n: slots })}
                        data-tooltip={t("bom.expandSlots", {
                          line: item.position_code,
                          n: slots,
                        })}
                        onClick={() => toggle(item.id)}
                      >
                        {t("tree.slots", { n: slots })}
                        <ChevronIcon size={12} />
                      </button>
                    )}
                  </td>
                  <td>
                    {item.material_info}
                    <LineAttributes attributes={item.attributes} />
                  </td>
                  <td className="mono">{item.drawing_no || "—"}</td>
                  <td>
                    {editable && parents.has(item.id) ? (
                      <select
                        aria-label={t("bom.moduleTypeOf", { line: item.position_code })}
                        value={item.module_type ?? ""}
                        disabled={setModuleType.isPending}
                        onChange={(event) =>
                          setModuleType.mutate({
                            itemId: item.id,
                            moduleType: event.target.value || null,
                          })
                        }
                      >
                        <option value="">{t("bom.notAModule")}</option>
                        {(moduleTypes.data ?? [])
                          .filter(
                            (type) => type.status === "ACTIVE" || type.code === item.module_type,
                          )
                          .map((type) => (
                            <option key={type.code} value={type.code}>
                              {moduleTypeLabel(
                                type.code,
                                moduleTypes.data ?? [],
                                i18n.language === "en-US",
                              )}
                            </option>
                          ))}
                      </select>
                    ) : item.module_type ? (
                      <span className="module-badge">
                        {moduleTypeLabel(
                          item.module_type,
                          moduleTypes.data ?? [],
                          i18n.language === "en-US",
                        )}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="mono">{item.material_code ?? "—"}</td>
                  <td>{item.quantity}</td>
                  <td>{item.effective_quantity ?? "—"}</td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`critical-${item.position_code}`}
                      checked={item.is_critical}
                      disabled={!editable || setCritical.isPending}
                      onChange={(event) =>
                        setCritical.mutate({ itemId: item.id, isCritical: event.target.checked })
                      }
                    />
                  </td>
                  <td>
                    <BarcodeRulesCell
                      item={item}
                      count={barcodeRules.patternsOf(item.material_code).length}
                      canManage={canManageMasterData}
                      onOpen={setRulesOf}
                    />
                  </td>
                  <td className="line-remark-cell">
                    {/* Edited in place — click, type, Enter — because remarks are written line
                    after line, and the dialog makes that four actions each. On a published
                    version too: the remark is the one column publishing does not freeze. */}
                    <RemarkCell
                      remark={item.remark}
                      editable={canManageMasterData}
                      lineLabel={item.position_code}
                      onSave={async (remark) => {
                        await api.boms.setLineRemark(bomId, item.id, remark, remarkLocale);
                        await invalidate();
                      }}
                    />
                  </td>
                  {/* Only on a draft. A published version has nothing to edit at all — the
                  whole page says so and offers 派生 instead — so a column of greyed icons
                  on every line would repeat that once per row (the list-page rule of
                  §10.5 is for actions that apply to some rows and not others). */}
                  {editable && (
                    <td className="row-actions">
                      {pendingDelete === item.id ? (
                        <>
                          <IconButton
                            label={t("common.confirm")}
                            icon={<CheckIcon />}
                            danger
                            disabled={deleteItem.isPending}
                            onClick={() => deleteItem.mutate(item.id)}
                          />
                          <IconButton
                            label={t("common.cancel")}
                            icon={<CloseIcon />}
                            onClick={() => setPendingDelete(null)}
                          />
                        </>
                      ) : (
                        <>
                          <IconButton
                            label={t("common.edit")}
                            icon={<EditIcon />}
                            onClick={() => setEditing(item)}
                          />
                          <IconButton
                            label={t("common.delete")}
                            icon={<TrashIcon />}
                            danger
                            onClick={() => setPendingDelete(item.id)}
                          />
                        </>
                      )}
                    </td>
                  )}
                </tr>
                {open &&
                  Array.from({ length: slots }, (_, index) => index + 1).map((slotNo) => (
                    <tr key={`${item.id}#${slotNo}`} className="slot-row">
                      <td />
                      <td className="mono" style={{ paddingLeft: "36px" }}>
                        {item.position_code}#{slotNo}
                      </td>
                      <td className="muted">
                        {item.display_name || item.material_info}#{slotNo}
                      </td>
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td />
                      <td className="line-remark-cell">
                        <RemarkCell
                          remark={slotRemarkOf(item, slotNo)}
                          editable={canManageMasterData}
                          lineLabel={`${item.position_code}#${slotNo}`}
                          onSave={async (remark) => {
                            await api.boms.setSlotRemark(
                              bomId,
                              item.id,
                              slotNo,
                              remark,
                              remarkLocale,
                            );
                            await invalidate();
                          }}
                        />
                      </td>
                      {editable && <td />}
                    </tr>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
      </ResizableTable>

      {rulesOf !== null && (
        <BarcodeRulesDialog
          materialCode={rulesOf}
          lines={detail.items
            .filter((item) => item.is_critical && item.material_code?.trim() === rulesOf)
            .map((item) => item.position_code)}
          rules={barcodeRules.byMaterial.get(rulesOf) ?? []}
          editable={canManageMasterData}
          onClose={() => setRulesOf(null)}
        />
      )}

      {detail.items.length > 0 && shownItems.length === 0 && (
        <p className="muted">{t("bom.noMatchingLines")}</p>
      )}
      {detail.items.length === 0 && (
        <p className="muted">{isDraft ? t("bom.emptyDraft") : t("common.empty")}</p>
      )}

      {editable && (
        <div className="filters">
          {/* A draft nobody published is disposable, and saying so is kinder than leaving
              an abandoned version in the list forever. */}
          <button type="button" className="button-danger" onClick={() => setConfirmingDelete(true)}>
            {t("bom.deleteDraft")}
          </button>
        </div>
      )}

      {/* Numbering a draft: the engineers' own version (DVT1.1 → 1.1), checked by the
          server against every number this product type has ever used. */}
      {editingVersion !== null && (
        <Modal
          title={t("bom.setVersion")}
          onClose={() => {
            setEditingVersion(null);
            setVersion.reset();
          }}
          onSubmit={() => setVersion.mutate(editingVersion)}
          footer={
            <>
              <button type="submit" className="button-primary" disabled={setVersion.isPending}>
                {t("common.save")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingVersion(null);
                  setVersion.reset();
                }}
              >
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <div className="form-row">
            <label htmlFor="bom-version">{t("bom.version")}</label>
            <input
              id="bom-version"
              inputMode="decimal"
              value={editingVersion}
              placeholder={t("bom.versionPlaceholder")}
              onChange={(event) => setEditingVersion(event.target.value)}
            />
          </div>
          <p className="hint">{t("bom.versionHint")}</p>
          <ErrorBanner error={setVersion.error} />
        </Modal>
      )}

      {/* Publishing is confirmed, and it is where the version gets its name: the moment
          it becomes the one people file against and pick from a list. The slot count is
          the number §8.2 step 9 asks to be checked before it is frozen. */}
      {publishName !== null && (
        <Modal
          title={t("bom.publishTitle")}
          onClose={() => {
            setPublishName(null);
            publish.reset();
          }}
          onSubmit={() => publish.mutate(publishName)}
          footer={
            <>
              <button type="submit" className="button-primary" disabled={publish.isPending}>
                {t("bom.publish")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPublishName(null);
                  publish.reset();
                }}
              >
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <div className="form-row">
            <label htmlFor="publish-version">{t("bom.version")}</label>
            <input
              id="publish-version"
              required
              inputMode="decimal"
              value={publishVersion}
              placeholder={t("bom.versionPlaceholder")}
              onChange={(event) => setPublishVersion(event.target.value)}
            />
          </div>
          <div className="form-row">
            <label htmlFor="publish-name">{t("bom.name")}</label>
            <input
              id="publish-name"
              maxLength={NAME_MAX_LEN}
              value={publishName}
              placeholder={t("bom.namePlaceholder")}
              onChange={(event) => setPublishName(event.target.value)}
            />
          </div>
          <p className="hint">
            {t("bom.publishConfirm", { critical: criticalCount, slots: slotCount })}
          </p>
          {modules.data && modules.data.modules.length > 0 && (
            <>
              <p>
                {t("bom.publishModules", {
                  scans: modules.data.robot.scan_count,
                  modules: modules.data.modules.filter((module) => module.depth === 1).length,
                })}
              </p>
              <BomModulesSummary modules={modules.data} types={moduleTypes.data ?? []} />
            </>
          )}
          <ErrorBanner error={publish.error} />
        </Modal>
      )}

      {/* Confirmed in a dialog like every delete (§10.5), with the line count, and the
          refusal read there: the button sits under the whole table, and an error shown in
          the banner at the top of a 400-line BOM was, to the person clicking, no reaction. */}
      {confirmingDelete && (
        <Modal
          title={t("bom.deleteTitle")}
          onClose={() => {
            setConfirmingDelete(false);
            removeBom.reset();
          }}
          onSubmit={() => removeBom.mutate()}
          footer={
            <>
              <button type="submit" className="button-danger" disabled={removeBom.isPending}>
                {t("common.delete")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmingDelete(false);
                  removeBom.reset();
                }}
              >
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <p>
            {t("bom.deleteDraftConfirm", {
              version: detail.version ?? t("bom.unnumbered"),
              rows: detail.items.length,
            })}
          </p>
          <ErrorBanner error={removeBom.error} />
        </Modal>
      )}

      {editing && (
        <BomItemDialog
          item={editing === "new" ? null : editing}
          items={detail.items}
          remarkLabel={t("bom.remarkIn", { language: t(`bom.remarkLanguages.${remarkLocale}`) })}
          pending={saveItem.isPending}
          error={<ErrorBanner error={saveItem.error} />}
          onSave={(draft) => saveItem.mutate({ item: editing === "new" ? null : editing, draft })}
          moduleTypes={moduleTypes.data ?? []}
          onClose={() => {
            saveItem.reset();
            setEditing(null);
          }}
        />
      )}
    </section>
  );
}

/** `sort_path` is the materialized ancestry the server sorts on; its separators are depth. */
function depthOf(item: BomItem): number {
  return item.sort_path.split(".").length - 1;
}

/** How many slots a line expands into: its whole-machine quantity, if it is critical. */
function slotCountOf(item: BomItem): number {
  return item.is_critical ? (item.effective_quantity ?? 0) : 0;
}

function slotRemarkOf(item: BomItem, slotNo: number): string | null {
  return item.slot_remarks.find((entry) => entry.slot_no === slotNo)?.remark ?? null;
}

type LineKind = "all" | "critical" | "counted";

const LINE_KINDS: LineKind[] = ["all", "critical", "counted"];

function isShown(item: BomItem, kind: LineKind): boolean {
  return kind === "all" || (kind === "critical") === item.is_critical;
}

function lineMatches(item: BomItem, needle: string): boolean {
  return [
    item.position_code,
    item.material_info,
    item.part_name,
    item.drawing_no,
    item.material_code,
    item.remark,
  ].some((field) => field?.toLowerCase().includes(needle));
}

function slotMatches(item: BomItem, needle: string): boolean {
  return item.slot_remarks.some((entry) => entry.remark.toLowerCase().includes(needle));
}

/** Display order of an engineering BOM's descriptive columns; the remark goes last. */
const ATTRIBUTE_ORDER = ["module", "material_type", "standard", "material", "unit", "source"];

/** An engineering BOM line's descriptive columns, read-only under its description
 *  (component design §4.6.4). The engineering remark is purchasing's note — shown here,
 *  labelled, and never to whoever scans at filing. */
function LineAttributes({ attributes }: { attributes?: Record<string, string> }) {
  const { t } = useTranslation();
  if (!attributes || Object.keys(attributes).length === 0) return null;
  const facts = ATTRIBUTE_ORDER.map((key) => attributes[key]).filter(Boolean);
  const remark = attributes.engineering_remark;
  return (
    <div className="hint line-attributes">
      {facts.join(" · ")}
      {remark && (
        <div>
          {t("bom.engineeringRemark")}: {remark}
        </div>
      )}
    </div>
  );
}

/**
 * A critical line's barcode rules: how many its material has, opening them. Only a
 * critical line is scanned, and only a material code has rules — a line without one says
 * so rather than offering a rule it could not keep.
 */
function BarcodeRulesCell({
  item,
  count,
  canManage,
  onOpen,
}: {
  item: BomItem;
  count: number;
  canManage: boolean;
  onOpen: (materialCode: string) => void;
}) {
  const { t } = useTranslation();
  const code = item.material_code?.trim();
  if (!item.is_critical) return null;
  if (!code) return <span className="muted">{t("bom.barcodeRulesNoMaterial")}</span>;
  if (count === 0 && !canManage) return <span className="muted">—</span>;
  return (
    <button
      type="button"
      className="link-button"
      aria-label={t("bom.barcodeRulesOf", { line: item.position_code })}
      onClick={() => onOpen(code)}
    >
      {count > 0 ? t("bom.barcodeRuleCount", { n: count }) : t("bom.barcodeRulesAdd")}
    </button>
  );
}
